<template>
  <div class="carousel-wrapper mt-20 mx-auto text-center">
    <!-- Custom Carousel Component -->
    <div class="custom-carousel" ref="carouselRef">
      <div class="carousel-container">
        <!-- Carousel items -->
        <transition name="slide" mode="out-in">
          <div
            v-if="carouselItems.length > 0"
            :key="currentIndex"
            class="carousel-item"
          >
            <figure class="flex justify-center items-center overflow-hidden mt-6">
              <img
                :src="carouselItems[currentIndex]?.sourceUrl"
                :alt="carouselItems[currentIndex]?.title"
                class="carousel-image"
              />
            </figure>

            <div class="carousel-content">
              <p v-if="carouselItems[currentIndex]?.title" class="cta-text">
                {{ carouselItems[currentIndex].title }}
              </p>
              <p
                v-if="!carouselItems[currentIndex]?.linkUrl"
                class="cta-subtext"
              >
                {{ carouselItems[currentIndex].text }}
              </p>
              <a
                v-if="
                  carouselItems[currentIndex]?.text &&
                  carouselItems[currentIndex]?.linkUrl
                "
                :href="carouselItems[currentIndex].linkUrl"
                class="inline-block cta-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ carouselItems[currentIndex].text }}
              </a>
            </div>
          </div>
        </transition>
      </div>

      <!-- Indicators -->
      <div class="carousel-indicators" v-if="carouselItems.length > 1">
        <button
          v-for="(item, index) in carouselItems"
          :key="index"
          :class="['indicator', { active: currentIndex === index }]"
          @click="goToSlide(index)"
          :aria-label="`Go to slide ${index + 1}`"
        ></button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import commonService from "@/services/commonService";
import { useI18n } from "vue-i18n";

const carouselItems = ref([]);
const autoSlideInterval = ref(5000);
const carouselRef = ref(null);
const currentIndex = ref(0);
let intervalId = null;

const { locale } = useI18n();

const goToSlide = (index) => {
  currentIndex.value = index;
  resetAutoPlay();
};

const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % carouselItems.value.length;
};

const startAutoPlay = () => {
  if (carouselItems.value.length > 1) {
    intervalId = setInterval(nextSlide, autoSlideInterval.value);
  }
};

const stopAutoPlay = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const resetAutoPlay = () => {
  stopAutoPlay();
  startAutoPlay();
};

const fetchCarousel = async () => {
  const lang = locale.value === "es" ? "es-US" : locale.value;

  try {
    const data = await commonService.fetchCarouselData(lang);
    carouselItems.value = data?.items || [];
    autoSlideInterval.value = data.autoSlideInterval || 5000;
    currentIndex.value = 0;
    resetAutoPlay();
  } catch (error) {
    console.error("Error fetching carousel:", error);
  }
};

onMounted(() => {
  fetchCarousel();
});

onUnmounted(() => {
  stopAutoPlay();
});

// 🟡 Re-fetch when locale changes
watch(locale, () => {
  fetchCarousel();
});
</script>

<style scoped>
.custom-carousel {
  position: relative;
  width: 100%;
  min-height: 500px;
}

.carousel-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.carousel-item {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.carousel-image {
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

.carousel-content {
  margin-top: 1rem;
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.5s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Carousel Indicators */
.carousel-indicators {
  position: absolute;
  bottom: -72px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: 10px;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  border: 2px solid #bbb;
  background-color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.indicator:hover {
  border-color: #999;
}

.indicator.active {
  background-color: #bbb;
  border-color: #bbb;
}

.cta-text {
  align-self: stretch;
  flex-grow: 0;
  font-size: 24px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.3;
  letter-spacing: normal;
  text-align: center;
  color: #2e2e2e;
  margin-bottom: 20px !important;
}

.cta-subtext {
  font-size: 16px;
  color: #555;
  opacity: 0.9;
}

.cta-button {
  background-color: #f7c053;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  text-decoration: none;
  color: #553c13;
}

.cta-button:hover {
  background-color: #d58f00;
}

@media (max-width: 600px) {
  .carousel-image {
    width: 100% !important;
    height: auto !important;
    max-height: 300px;
    object-fit: contain !important;
  }

  .custom-carousel {
    min-height: 400px;
  }
}
</style>
